import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useApi from 'services/mavapi/useApi';
import {
  ElementaryPoints,
  GeneralLookup,
  HighSchoolTeam,
  LeaderPoint,
  ManageShowService,
  RiderPoint,
  RiderPointListWrapper,
  ScoreBoard,
  ShowRegistration,
  ShowRider,
  ShowWrapper,
  ShowRiderListWrapper,
  RiderInfo,
  RiderSplitClass,
  GeneratedSplit,
  ShowsCategorizedAddRiderList,
  GroupedShowRiderListWrapper,
  GroupedShowRiderPointWrapper,
  ReportService,
} from 'services/openapi';
import { useNavigateToRoute } from 'utils/common';
import Cookies from 'js-cookie';

const useShowApi = () => {
  const userType = Cookies.get('userType') ?? '';
  const [loading, setLoading] = useState(false);
  // Dedicated loader for the rider-points list so it stays independent from
  // the other APIs (high-points, score-board, etc.) that fire at the same time.
  const [riderPointsLoading, setRiderPointsLoading] = useState(false);
  // Dedicated loaders for each tab's data API
  const [highPointLoading, setHighPointLoading] = useState(false);
  const [elementaryHighPointLoading, setElementaryHighPointLoading] =
    useState(false);
  const [scoreBoardLoading, setScoreBoardLoading] = useState(false);
  const [leaderPointLoading, setLeaderPointLoading] = useState(false);
  const [showDataLoading, setShowDataLoading] = useState(false);
  const [, setError] = useState<unknown | null>();
  const { error: apiError, handleRequest } = useApi();
  const {
    manageShowGetShows,
    manageShowDeleteShow,
    manageShowShowStatus,
    manageShowCreateShow,
    manageShowUpdateShow,
    manageShowGetShowInfo,
    manageShowUploadShowImage,
    manageShowAddRiderToShow,
    manageShowGetAllRiders,
    manageShowGetShowRiderList,
    manageShowDeleteRider,
    manageShowGetShowRiderPoints,
    manageShowAddRiderPoints,
    manageShowGetSeniorJuniorHighPoints,
    manageShowGetShowScoreBoard,
    manageShowGetLeaderPoint,
    manageShowGetRiderElmentaryHighPoint,
    manageShowGetShowRiderInfo,
    manageShowUpdateRiderSplit,
    manageShowGenerateSplit,
    manageShowExportShowsList,
    manageShowGetRiderList,
    manageShowDuplicateShow,
    manageShowGetGroupedShowRiderList,
    manageShowGetGroupedShowRiderPoints,
    manageShowDownloadJudgesCard,
  } = ManageShowService;
  const { reportGetShowResultReport } = ReportService;
  const [showDataList, setShowDataList] = useState<ShowWrapper>();
  const [isResponseRider, setIsResponseRider] = useState(false);
  const [riderDataList, setRiderDataList] = useState<GeneralLookup[]>();
  const [showRiderList, setShowRiderList] = useState<
    Array<ShowsCategorizedAddRiderList>
  >([]);
  const [showInfoData, setShowInfoData] = useState<ShowRegistration>();
  const { actions } = useErrorHandlingSlice();
  const dispatch = useDispatch();
  const redirect = useNavigateToRoute();
  const [showRiderDataList, setShowRiderDataList] =
    useState<GroupedShowRiderListWrapper>();
  const [riderShowList, setRiderShowList] =
    useState<GroupedShowRiderPointWrapper>();
  const [highPointList, setHighPointList] = useState<HighSchoolTeam>();
  const [showScoreBoardList, setShowScoreBoardList] = useState<ScoreBoard>();
  const [showLeaderPointList, setShowLeaderPointList] = useState<LeaderPoint>();
  const [showElementaryHighPointList, setShowElementaryHighPointList] =
    useState<ElementaryPoints>();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showRiderInfo, setShowRiderInfo] = useState<RiderInfo>();
  const [splitData, setSplitData] = useState<Array<GeneratedSplit>>();
  const [errorData, setErrorData] = useState<ShowRider>();
  const handleDownloadJudgesCard = (showId: string, documentType?: string) => {
    setDownloadLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(manageShowDownloadJudgesCard(showId, documentType))
        .then(res => {
          resolve(res);
          setDownloadLoading(false);
          setLoading(false);
        })
        .catch(() => {
          setDownloadLoading(false);
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const handleDownloadShows = (
    season?: string,
    location?: Array<string>,
    categoryFilter?: Array<string>,
    divisionFilter?: Array<string>,
    status?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
  ) => {
    setDownloadLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(
        manageShowExportShowsList(
          season,
          location,
          categoryFilter,
          divisionFilter,
          status,
          searchInput,
          sortBy,
          sortOrder,
        ),
      )
        .then(res => {
          resolve(res);
          setDownloadLoading(false);
          setLoading(false);
        })
        .catch(() => {
          setDownloadLoading(false);
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const handleDownloadShowDetails = (seasonId?: string, showId?: string) => {
    setDownloadLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(reportGetShowResultReport(seasonId, showId))
        .then(res => {
          resolve(res);
          setDownloadLoading(false);
          setLoading(false);
        })
        .catch(() => {
          setDownloadLoading(false);
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const fetchManageGetShows = (
    season?: string,
    location?: Array<string>,
    categoryFilter?: Array<string>,
    divisionFilter?: Array<string>,
    status?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 1,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    let pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageShowGetShows(
        season,
        location,
        categoryFilter,
        divisionFilter,
        status,
        searchInput,
        sortBy,
        sortOrder,
        pageNumCall,
        pageSize,
      ),
    )
      .then(res => {
        if (res) {
          setShowDataList(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const deleteShow = (
    id?: string,
    season?: string,
    location?: Array<string>,
    categoryFilter?: Array<string>,
    divisionFilter?: Array<string>,
    status?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 1,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(manageShowDeleteShow(id ?? ''))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Show deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
          // Calculate the correct page after deletion
          // If current page is empty after deletion, go to previous page
          const itemsOnCurrentPage = showDataList?.shows?.length ?? 0;
          let nextPageNumber = pageNumber;

          // If we're on a page with only 1 item and we just deleted it, go to previous page
          if (itemsOnCurrentPage <= 1 && pageNumber > 0) {
            nextPageNumber = pageNumber - pageSize;
          }

          fetchManageGetShows(
            season,
            location,
            categoryFilter,
            divisionFilter,
            status,
            searchInput,
            sortBy,
            sortOrder,
            nextPageNumber,
            pageSize,
          );
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };

  const fetchShowActiveOrInactive = (
    id: string,
    activeStatusId?: string,
    item?: string,
    season?: string,
    location?: Array<string>,
    categoryFilter?: Array<string>,
    divisionFilter?: Array<string>,
    status?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 1,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(manageShowShowStatus(id, activeStatusId))
      .then(res => {
        if (res) {
          fetchManageGetShows(
            season,
            location,
            categoryFilter,
            divisionFilter,
            status,
            searchInput,
            sortBy,
            sortOrder,
            pageNumber,
            pageSize,
          );

          if (item === 'Make active') {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Active',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Inactive',
                show: true,
                errorType: 'success',
              }),
            );
          }
          setLoading(false);
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const getShowInfo = (id: string) => {
    setShowDataLoading(true);
    handleRequest(manageShowGetShowInfo(id))
      .then(res => {
        setShowInfoData(res);
        setShowDataLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setShowDataLoading(false);
      });
  };

  const createShowInfo = (requestBody: ShowRegistration, file?: Blob) => {
    const payload: ShowRegistration = {
      ...requestBody,
      natInvite:
        requestBody.natInvite?.id === '' ? undefined : requestBody.natInvite,
      state: requestBody.state?.id === '' ? undefined : requestBody.state,
    };
    setLoading(true);
    handleRequest(manageShowCreateShow(payload))
      .then(res => {
        setLoading(false);
        if (res && res.code !== 'InternalServerError') {
          if (file) {
            let formData = { file: file };
            uploadShowProfileImage(res, formData);
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Show Created Successfully',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            Cookies.remove('showId');

            redirect(
              userType === 'COACH'
                ? `/coach/shows/view/${res}/show-info`
                : `/admin/shows/view/${res}/show-info`,
            );
          }
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const createDuplicateShow = (requestBody: ShowRegistration, file?: Blob) => {
    const payload: ShowRegistration = {
      ...requestBody,
      natInvite:
        requestBody.natInvite?.id === '' ? undefined : requestBody.natInvite,
      state: requestBody.state?.id === '' ? undefined : requestBody.state,
    };
    setLoading(true);
    handleRequest(manageShowDuplicateShow(payload))
      .then(res => {
        setLoading(false);
        if (res && res.code !== 'InternalServerError') {
          if (file) {
            let formData = { file: file };
            uploadShowProfileImage(res, formData);
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Show Created Successfully',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            Cookies.remove('showId');

            redirect(
              userType === 'COACH'
                ? `/coach/shows/view/${res}/show-info`
                : `/admin/shows/view/${res}/show-info`,
            );
          }
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const updateShowInfo = (requestBody: ShowRegistration, file?: Blob) => {
    setLoading(true);
    const payload: ShowRegistration = {
      ...requestBody,
      natInvite:
        requestBody.natInvite?.id === '' ? undefined : requestBody.natInvite,
      state: requestBody.state?.id === '' ? undefined : requestBody.state,
    };
    handleRequest(manageShowUpdateShow(payload))
      .then(res => {
        setLoading(false);
        redirect(
          userType === 'COACH'
            ? `/coach/shows/view/${res}/show-info`
            : `/admin/shows/view/${res}/show-info`,
        );
        if (file) {
          let formData = { file: file };
          uploadShowProfileImage(res, formData);
        } else {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Show Updated Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          redirect(
            userType === 'COACH'
              ? `/coach/shows/view/${res}/show-info`
              : `/admin/shows/view/${res}/show-info`,
          );
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const uploadShowProfileImage = (
    id: string,
    formData?: {
      file?: Blob;
    },
  ) => {
    setLoading(true);
    handleRequest(manageShowUploadShowImage(id, formData))
      .then(res => {
        Cookies.remove('showId');
        setLoading(false);
        redirect(
          userType === 'COACH'
            ? `/coach/shows/view/${id}/show-info`
            : `/admin/shows/view/${id}/show-info`,
        );
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Updated Show Image',
            show: true,
            errorType: 'success',
          }),
        );
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const GetRiderListInShows = (
    teamId?: string,
    categoryId?: string,
    divisionId?: string,
  ) => {
    setLoading(true);
    handleRequest(manageShowGetAllRiders(teamId, categoryId, divisionId))
      .then(res => {
        if (res) {
          setRiderDataList(res);
          setIsResponseRider(true);
        } else {
          setIsResponseRider(false);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const fetchManageGetShowRiderList = (
    showId: string,
    teamFilter?: string[],
    categoryId?: string[] | undefined,
    divisionId?: string[] | undefined,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(
      manageShowGetGroupedShowRiderList(
        showId,
        teamFilter,
        categoryId,
        divisionId,
        searchInput,
      ),
    )
      .then(res => {
        if (res) {
          setShowRiderDataList(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const SaveRiderInShow = (requestBody?: ShowRider, cookieShowId?: string) => {
    setLoading(true);
    handleRequest(manageShowAddRiderToShow(requestBody))
      .then(res => {
        setLoading(false);
        if (res === 'Success') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Saved successfully',
              show: true,
              errorType: 'success',
            }),
          );
          redirect(
            userType === 'COACH'
              ? `/coach/shows/view/${cookieShowId}/manage-rider`
              : `/admin/shows/view/${cookieShowId}/manage-rider`,
          );
        } else {
          setErrorData(res);
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const deleteShowRider = (
    riderId?: string,
    showId?: string,
    teamFilter?: Array<string>,
    categoryId?: Array<string>,
    divisionId?: Array<string>,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 1,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(manageShowDeleteRider(riderId as string))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Rider deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
          fetchManageGetShowRiderList(
            showId ?? '',
            teamFilter,
            categoryId,
            divisionId,
            searchInput,
            sortBy,
            sortOrder,
            pageNumber,
            pageSize,
          );
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };
  const getShowRiderPoints = (
    showId: string,
    divisionId?: string,
    categoryId?: string,
    classFilter?: Array<string>,
    sortBy: string = 'team',
    sortOrder?: number,
  ) => {
    setRiderPointsLoading(true);
    handleRequest(
      manageShowGetGroupedShowRiderPoints(
        showId,
        divisionId,
        categoryId,
        classFilter,
        sortBy,
        sortOrder,
      ),
    )
      .then(res => {
        setRiderPointsLoading(false);
        if (res) {
          setRiderShowList(res);
        }
      })
      .catch(() => {
        setError(apiError);
        setRiderPointsLoading(false);
      });
  };
  const addRiderPoint = (
    requestBody: RiderPoint[],
    listParameters: {
      showId: string;
      divisionId: string;
      categoryId: string;
      classFilter: string[] | undefined;
      pageNumber: number | undefined;
    },
  ): Promise<boolean> => {
    setLoading(true);
    return handleRequest(manageShowAddRiderPoints(requestBody))
      .then(res => {
        setLoading(false);
        if (res === 'Success') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Point added successfully',
              show: true,
              errorType: 'success',
            }),
          );
          return true;
        } else {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'something went wrong',
              show: true,
              errorType: 'error',
            }),
          );
          return false;
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
        return false;
      });
  };

  const getSeniorJuniorHighPoints = (showId: string) => {
    setHighPointLoading(true);
    handleRequest(manageShowGetSeniorJuniorHighPoints(showId))
      .then(res => {
        setHighPointLoading(false);
        setHighPointList(res);
      })
      .catch(() => {
        setError(apiError);
        setHighPointLoading(false);
      });
  };

  const getShowScoreBoard = (showId: string) => {
    setScoreBoardLoading(true);
    handleRequest(manageShowGetShowScoreBoard(showId))
      .then(res => {
        setScoreBoardLoading(false);
        setShowScoreBoardList(res);
      })
      .catch(() => {
        setError(apiError);
        setScoreBoardLoading(false);
      });
  };

  const getShowLeaderPoint = (showId: string) => {
    setLeaderPointLoading(true);
    handleRequest(manageShowGetLeaderPoint(showId))
      .then(res => {
        setLeaderPointLoading(false);
        setShowLeaderPointList(res);
      })
      .catch(() => {
        setError(apiError);
        setLeaderPointLoading(false);
      });
  };

  const getShowElementaryHighPoint = (showId: string) => {
    setElementaryHighPointLoading(true);
    handleRequest(manageShowGetRiderElmentaryHighPoint(showId))
      .then(res => {
        setElementaryHighPointLoading(false);
        setShowElementaryHighPointList(res);
      })
      .catch(() => {
        setError(apiError);
        setElementaryHighPointLoading(false);
      });
  };
  const getShowRiderInfo = (riderId: string) => {
    handleRequest(manageShowGetShowRiderInfo(riderId))
      .then(res => {
        setShowRiderInfo(res);
      })
      .catch(() => {
        setError(apiError);
      });
  };
  const updateClassSplitApi = (
    isGenerateSplit: boolean,
    requestBody?: Array<RiderSplitClass>,
    showId?: string,
    teamFilter?: Array<string>,
    categoryId?: Array<string>,
    divisionId?: Array<string>,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 1,
    pageSize: number = 10,
    isManagerRiderList?: boolean,
  ): Promise<boolean> => {
    setLoading(true);
    return handleRequest(manageShowUpdateRiderSplit(requestBody))
      .then(res => {
        setLoading(false);
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Updated successfully',
              show: true,
              errorType: 'success',
            }),
          );
          if (isGenerateSplit === true) {
            redirect(
              userType === 'COACH'
                ? `/coach/shows/view/${showId}/manage-rider`
                : `/admin/shows/view/${showId}/manage-rider`,
            );
          } else if (isManagerRiderList) {
            fetchManageGetShowRiderList(
              showId ?? '',
              teamFilter,
              categoryId,
              divisionId,
              searchInput,
              sortBy,
              sortOrder,
              pageNumber,
              pageSize,
            );
          }
          return !!res;
        }
        return false;
      })
      .catch(() => {
        setLoading(false);
        setError(apiError);
        return false;
      });
  };
  const getGenerateSplitApi = (showId: string) => {
    setLoading(true);
    handleRequest(manageShowGenerateSplit(showId))
      .then(res => {
        setLoading(false);
        setSplitData(res);
      })
      .catch(() => {
        setLoading(false);
        setError(apiError);
      });
  };
  const GetRiderListShows = (
    showId?: string,
    teamId?: string,
    categoryId?: string,
    divisionId?: string,
  ) => {
    setLoading(true);
    handleRequest(
      manageShowGetRiderList(showId, teamId, categoryId, divisionId),
    )
      .then(res => {
        if (res) {
          setShowRiderList(res);
          setIsResponseRider(true);
        } else {
          setIsResponseRider(false);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  return {
    loading,
    riderPointsLoading,
    highPointLoading,
    elementaryHighPointLoading,
    scoreBoardLoading,
    leaderPointLoading,
    fetchManageGetShows,
    showDataList,
    deleteShow,
    fetchShowActiveOrInactive,
    showInfoData,
    getShowInfo,
    createShowInfo,
    updateShowInfo,
    showDataLoading,
    GetRiderListInShows,
    setRiderDataList,
    riderDataList,
    SaveRiderInShow,
    fetchManageGetShowRiderList,
    showRiderDataList,
    deleteShowRider,
    isResponseRider,
    getShowRiderPoints,
    riderShowList,
    addRiderPoint,
    getSeniorJuniorHighPoints,
    highPointList,
    showScoreBoardList,
    getShowScoreBoard,
    getShowLeaderPoint,
    showLeaderPointList,
    getShowElementaryHighPoint,
    showElementaryHighPointList,
    getShowRiderInfo,
    showRiderInfo,
    updateClassSplitApi,
    getGenerateSplitApi,
    splitData,
    setSplitData,
    errorData,
    handleDownloadShows,
    downloadLoading,
    GetRiderListShows,
    showRiderList,
    setShowRiderList,
    createDuplicateShow,
    handleDownloadJudgesCard,
    handleDownloadShowDetails,
  };
};
export default useShowApi;
